import React, {useEffect, useState} from 'react'
import {inject, observer} from 'mobx-react'
import {Redirect, useLocation, useParams} from 'react-router-dom'
import iltalehti from '../../../images/iltalehti_logo.svg'
import {ElectionLogo} from '../../elements/ElectionLogo'
import {LoaderIcon} from '../../elements/LoaderIcon'
import {Button} from '../../elements/Button'
import {AuthStore} from '../../stores/AuthStore'
import {CandidateStore} from '../../stores/CandidateStore'
import {theme} from '../../theme'
import './TunnusLoginPage.scss'

interface ITunnusLoginPageProps {
  authStore: AuthStore
  candidateStore: CandidateStore
}

const TunnusLoginContent: React.FC<{
  header: string
  content1: string | React.ReactNode
  content2?: string
  handleLogin?: () => Promise<void>
  status?: string
  showContactDetails?: boolean
}> = ({header, content1, content2, handleLogin, status, showContactDetails}) => {
  return (
    <>
      <div className="tunnus-login-page__header">{header}</div>
      <div className="tunnus-login-page__content-container">{content1}</div>
      {content2 && <div className="tunnus-login-page__content-container">{content2}</div>}
      {handleLogin && status && (
        <div className="tunnus-login-page__content-container">
          <Button height="40px" fontSize="18px" disabled={status === 'tunnus_redirect'} onClick={handleLogin}>
            {status === 'tunnus_redirect' ? (
              <div className="tunnus-login-page__loader">
                <LoaderIcon />
              </div>
            ) : (
              <span>Kirjaudu</span>
            )}
          </Button>
        </div>
      )}
      {showContactDetails && (
        <div className="tunnus-login-page__content-container">
          Mikäli kirjautumisessa ilmenee ongelmia, voitte olla yhteydessä ehdokastukeemme osoitteessa{' '}
          <a href="mailto:palaute.vaalikone@almamedia.fi">palaute.vaalikone@almamedia.fi</a>.
        </div>
      )}
    </>
  )
}

const TunnusLoginPage: React.FC<ITunnusLoginPageProps> = inject(
  'authStore',
  'candidateStore'
)(
  observer(({authStore, candidateStore}) => {
    const [isLoading, setIsLoading] = useState(true)
    const {dataString} = useParams<{dataString: string}>()
    const {search} = useLocation()
    const {status} = authStore

    const handleLogin = async () => {
      if (dataString && status === 'valid_data') {
        sessionStorage.setItem('dataString', dataString)
        await authStore?.tunnusLogin()
      }
    }

    const getRedirectUrl = () => {
      return `/ehdokas/${candidateStore.tunnusCandidate?.tunnus_id}`
    }

    useEffect(() => {
      ;(async () => {
        try {
          if (status === 'unauthorized' && dataString) {
            await authStore.validateLoginData(decodeURIComponent(dataString))
          } else if (!dataString) {
            await authStore.tunnusLoginResultHandler()
          }
        } catch (error) {
          console.error(error) // eslint-disable-line no-console
        } finally {
          setIsLoading(false)
        }
      })()
    }, [status, dataString]) // eslint-disable-line react-hooks/exhaustive-deps

    if (status === 'tunnus_redirect_done') {
      return <Redirect to={getRedirectUrl()} />
    }

    if (search?.includes('code')) {
      return null
    }

    return (
      <div className="tunnus-login-page">
        <div className="tunnus-login-page__il-logo">
          <img src={iltalehti} />
        </div>
        <ElectionLogo />
        <div className="tunnus-login-page__container">
          {(() => {
            if (isLoading) {
              return <LoaderIcon height={77} color={theme.color.red} />
            }

            switch (status) {
              case 'valid_data':
              case 'tunnus_redirect':
                return (
                  <TunnusLoginContent
                    header="Tervetuloa Iltalehden vaalikoneeseen!"
                    content1={
                      <>
                        Kirjaudu sisään vastataksesi vaalikoneeseen.
                        <br />
                        Kirjautuminen vaatii vahvan tunnistautumisen.
                      </>
                    }
                    content2="Vaalikonetta voi täyttää perjantaihin 21.3. asti, minkä jälkeen tämä ehdokaskäyttöliittymä sulkeutuu. Huomaathan kuitenkin, että vaalikone avautuu yleisölle jo perjantaina 14.3."
                    handleLogin={handleLogin}
                    status={status}
                    showContactDetails={true}
                  />
                )
              case 'election_closed':
                return (
                  <TunnusLoginContent
                    header="Vaalikoneen ehdokaskäyttöliittymä on suljettu"
                    content1={
                      <>
                        Mikäli tiedoissasi on muutettavaa, voitte olla yhteydessä ehdokastukeemme osoitteessa{' '}
                        <a href={'mailto:palaute.vaalikone@almamedia.fi'}>palaute.vaalikone@almamedia.fi</a>.
                      </>
                    }
                  />
                )
              case 'invalid_data':
                return (
                  <TunnusLoginContent
                    header="Linkki on virheellinen"
                    content1="Tarkista linkki ja yritä uudelleen."
                    showContactDetails={true}
                  />
                )
              default: {
                const token = sessionStorage.getItem('dataString') || ''
                return <Redirect to={`/kirjaudu/${token}`} />
              }
            }
          })()}
        </div>
      </div>
    )
  })
)

export default TunnusLoginPage
